"use client";

import { createContext, useContext, useLayoutEffect, useRef, useTransition, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

type PdpVariantNavigation = {
	pending: boolean;
	selectVariant: (variantId: string) => void;
	canSubmitVariant: (variantId: string) => boolean;
};

const VariantNavigationContext = createContext<PdpVariantNavigation | null>(null);

/** One transition and immediate guard for every purchase path in the PDP form. */
export function PdpVariantNavigationProvider({
	confirmedVariantId,
	children,
}: {
	confirmedVariantId: string | undefined;
	children: ReactNode;
}) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [pending, startTransition] = useTransition();
	const blockedRef = useRef(false);
	const confirmedRef = useRef(confirmedVariantId);

	useLayoutEffect(() => {
		blockedRef.current = pending;
		confirmedRef.current = confirmedVariantId;
	}, [pending, confirmedVariantId]);

	const selectVariant = (variantId: string) => {
		if (!variantId || blockedRef.current || variantId === confirmedRef.current) return;
		const params = new URLSearchParams(searchParams);
		params.set("variant", variantId);
		// Form actions and requestSubmit may run before React commits pending UI.
		blockedRef.current = true;
		startTransition(() => {
			// React forwards navigation errors to the existing error boundary. Keep
			// the immediate guard set until a commit: startTransition can catch a
			// synchronous throw and return before that error boundary renders.
			router.replace(`${pathname}?${params.toString()}`, { scroll: false });
		});
	};

	const canSubmitVariant = (variantId: string) =>
		!blockedRef.current && Boolean(variantId) && variantId === confirmedRef.current;

	return (
		<VariantNavigationContext.Provider value={{ pending, selectVariant, canSubmitVariant }}>
			{children}
		</VariantNavigationContext.Provider>
	);
}

export function usePdpVariantNavigation(): PdpVariantNavigation | null {
	return useContext(VariantNavigationContext);
}
